/**
 * How grown a word looks in the collection ([Review](../../../docs/REVIEW.md)).
 *
 * Derived from spaced-repetition state rather than stored alongside it
 * ([D-032](../../../docs/DECISIONS.md#d-032)), so the picture a child sees can
 * never disagree with what the engine believes.
 *
 * With one exception, and it is the whole reason `high_water_stage` exists: **a
 * word never moves backwards on this screen.** When stability decays the word
 * quietly comes back for review, but the card stays where it got to. Watching
 * your own vocabulary shrink is exactly the kind of small discouragement this
 * app is built to avoid — the engine can know without the child being told.
 */
export const STAGE_NAMES = {
    0: 'New',
    1: 'Learning',
    2: 'Known',
    3: 'Strong',
};
/**
 * Stability, in days, at which a word reads as Known and then as Strong.
 *
 * Stability is not the interval: the scheduler clamps a child's intervals to
 * sixty days ([D-014](../../../docs/DECISIONS.md#d-014)) while the underlying
 * stability keeps climbing, so Strong stays reachable even though a review is
 * never further off than two months.
 */
export const KNOWN_STABILITY_DAYS = 21;
export const STRONG_STABILITY_DAYS = 60;
/** Reviews that went well. A lapse is a review, but it is not a success. */
export const successfulReviews = (state) => Math.max(0, state.reps - state.lapses);
/**
 * The stage this word's current state earns.
 *
 * Checked strongest first: a word with high stability is Known or Strong however
 * few times it has been seen, and a word that has been reviewed successfully but
 * has not settled yet is Learning rather than New.
 */
export function collectionStage(state) {
    if (state.stability >= STRONG_STABILITY_DAYS)
        return 3;
    if (state.stability >= KNOWN_STABILITY_DAYS)
        return 2;
    return successfulReviews(state) >= 1 ? 1 : 0;
}
/**
 * The stage to actually draw: the best this word has ever been.
 *
 * `highWater` is what the database stores. Everything else on the card comes
 * from live state; this one number is kept because "the best it has ever been"
 * is not recoverable from a state that has since decayed.
 */
export const displayStage = (state, highWater) => {
    const now = collectionStage(state);
    const best = Math.max(now, Math.min(Math.max(Math.round(highWater), 0), 3));
    return best;
};
//# sourceMappingURL=collection.js.map